import { useEffect, useState } from 'react';
import ConfettiExplosion from 'react-confetti-explosion';
import VisibleIf from '../../components/VisibleIf';
import { NUMBER_OF_LOTTERY_FIELDS_TO_SCRATCH } from '../../utils';
import styles from './scratch-lottery-ticket.module.less';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import RoundButton from '../../components/round-button/RoundButton';
import loadable from '@loadable/component';
import { LOTTERY_TICKET_TYPES } from './lottery-ticket-model';
import Image from '../../components/image/Image';

const LotteryTicket = loadable(() => import('./LotteryTicket'));

const getLotteryTypeDefinition = (type) => {
    switch (type) {
        case LOTTERY_TICKET_TYPES.CHRISTMAS:
            return {
                mainBackgroundStyle: styles.christmasLotteryBackground,
                placeholderImagesDirectory: 'https://static.spleis.no/images/scratch/christmas',
                lotteryImages: [
                    'https://static.spleis.no/images/scratch/christmas/Julegave.svg',
                    'https://static.spleis.no/images/scratch/christmas/Nisselue.svg',
                    'https://static.spleis.no/images/scratch/christmas/Akebrett.svg',
                    'https://static.spleis.no/images/scratch/christmas/Grisunge.svg',
                    'https://static.spleis.no/images/scratch/christmas/Julekule.svg',
                    'https://static.spleis.no/images/scratch/christmas/Lam.svg',
                    'https://static.spleis.no/images/scratch/christmas/Bamse.svg',
                    'https://static.spleis.no/images/scratch/christmas/Juletre.svg',
                    'https://static.spleis.no/images/scratch/christmas/Ugle.svg',
                    'https://static.spleis.no/images/scratch/christmas/Hund.svg',
                    'https://static.spleis.no/images/scratch/christmas/Katt.svg',
                    'https://static.spleis.no/images/scratch/christmas/ÅpenJulegave.svg',
                ],
            };
        case LOTTERY_TICKET_TYPES.WINTER:
            return {
                mainBackgroundStyle: styles.winterLotteryBackground,
                placeholderImagesDirectory: 'https://static.spleis.no/images/scratch/winter',
                lotteryImages: [
                    'https://static.spleis.no/images/scratch/winter/Akebrett.svg',
                    'https://static.spleis.no/images/scratch/winter/Bamse.svg',
                    'https://static.spleis.no/images/scratch/winter/Barn_ski.svg',
                    'https://static.spleis.no/images/scratch/winter/Elg.svg',
                    'https://static.spleis.no/images/scratch/winter/Hund.svg',
                    'https://static.spleis.no/images/scratch/winter/Kaffe.svg',
                    'https://static.spleis.no/images/scratch/winter/Lam.svg',
                    'https://static.spleis.no/images/scratch/winter/Skilue.svg',
                    'https://static.spleis.no/images/scratch/winter/Skøyte.svg',
                    'https://static.spleis.no/images/scratch/winter/Tre.svg',
                    'https://static.spleis.no/images/scratch/winter/Ugle.svg',
                ],
            };
        case LOTTERY_TICKET_TYPES.EASTER:
            return {
                mainBackgroundStyle: styles.easterLotteryBackground,
                placeholderImagesDirectory: 'https://static.spleis.no/images/scratch/easter',
                lotteryImages: [
                    'https://static.spleis.no/images/scratch/easter/Akebrett.svg',
                    'https://static.spleis.no/images/scratch/easter/Bamse.svg',
                    'https://static.spleis.no/images/scratch/easter/Barn_ski.svg',
                    'https://static.spleis.no/images/scratch/easter/Hund.svg',
                    'https://static.spleis.no/images/scratch/easter/Hytte.svg',
                    'https://static.spleis.no/images/scratch/easter/Kaffe.svg',
                    'https://static.spleis.no/images/scratch/easter/Lam.svg',
                    'https://static.spleis.no/images/scratch/easter/Paaskeegg.svg',
                    'https://static.spleis.no/images/scratch/easter/Paaskeegg2.svg',
                    'https://static.spleis.no/images/scratch/easter/Paaskekylling.svg',
                    'https://static.spleis.no/images/scratch/easter/Skoyte.svg',
                ],
            };
        case LOTTERY_TICKET_TYPES.STANDARD:
        default:
            return {
                mainBackgroundStyle: styles.standardLotteryBackground,
                placeholderImagesDirectory: 'https://static.spleis.no/images/scratch/standard',
                lotteryImages: [
                    'https://static.spleis.no/images/scratch/standard/Gris.svg',
                    'https://static.spleis.no/images/scratch/standard/Katt.svg',
                    'https://static.spleis.no/images/scratch/standard/Bamse.svg',
                    'https://static.spleis.no/images/scratch/standard/Sel.svg',
                    'https://static.spleis.no/images/scratch/standard/Kaktus.svg',
                    'https://static.spleis.no/images/scratch/standard/Gave.svg',
                    'https://static.spleis.no/images/scratch/standard/Is.svg',
                    'https://static.spleis.no/images/scratch/standard/Trommer.svg',
                    'https://static.spleis.no/images/scratch/standard/Bil.svg',
                    'https://static.spleis.no/images/scratch/standard/Badeball.svg',
                    'https://static.spleis.no/images/scratch/standard/Trompet.svg',
                ],
            };
    }
};

const ScratchLotteryTicket = ({
    winner,
    index,
    finito,
    alreadyRevealed,
    imageIndices,
    hideScratchAll = false,
    gotoNextTicket,
    gotoPreviousTicket,
    numberOfTickets,
    blur,
    showEndImage,
    didScratchAndWinOnCurrentTicket,
    type,
}) => {
    const [isFinished0, setIsFinished0] = useState(false);
    const [isFinished1, setIsFinished1] = useState(false);
    const [isFinished2, setIsFinished2] = useState(false);
    const [isFinished3, setIsFinished3] = useState(false);
    const [isFinished4, setIsFinished4] = useState(false);
    const [isFinished5, setIsFinished5] = useState(false);
    const [isFinished6, setIsFinished6] = useState(false);
    const [isFinished7, setIsFinished7] = useState(false);
    const [isFinished8, setIsFinished8] = useState(false);
    const [scratchAll, setScratchAll] = useState(false);

    const lotteryTypeDefinition = getLotteryTypeDefinition(type);

    useEffect(() => {
        setScratchAll(false);
    }, [index]);

    useEffect(() => {
        if (isFinished() && finito) {
            finito(true);
        }
    }, [
        isFinished0,
        isFinished1,
        isFinished2,
        isFinished3,
        isFinished4,
        isFinished5,
        isFinished6,
        isFinished7,
        isFinished8,
    ]);

    const setIsFinishedFunctions = [
        setIsFinished0,
        setIsFinished1,
        setIsFinished2,
        setIsFinished3,
        setIsFinished4,
        setIsFinished5,
        setIsFinished6,
        setIsFinished7,
        setIsFinished8,
    ];
    const finishedStates = [
        isFinished0,
        isFinished1,
        isFinished2,
        isFinished3,
        isFinished4,
        isFinished5,
        isFinished6,
        isFinished7,
        isFinished8,
    ];

    const isFinished = () => {
        for (let finishedState of finishedStates) {
            if (!finishedState) {
                return false;
            }
        }

        return true;
    };

    const lotteryTicketList = [];
    const areAllFieldsRevealed = finishedStates.filter((fieldIsFinished) => !fieldIsFinished).length === 0;
    for (let i = 0; i < NUMBER_OF_LOTTERY_FIELDS_TO_SCRATCH; i++) {
        const numberOfImages = imageIndices.filter((img) => img === imageIndices[i]).length;

        lotteryTicketList.push(
            <LotteryTicket
                key={`ticket${i}`}
                setIsFinishedFunction={setIsFinishedFunctions[i]}
                winner={winner}
                lotteryIndex={index}
                imageIndex={i}
                alreadyRevealed={alreadyRevealed}
                placeHolder={`${lotteryTypeDefinition.placeholderImagesDirectory}/skrap${i + 1}.svg`}
                image={lotteryTypeDefinition.lotteryImages[imageIndices[i]]}
                scratchAll={scratchAll}
                winningField={numberOfImages === 3 && areAllFieldsRevealed}
            />
        );
    }

    return (
        <>
            <VisibleIf isVisible={isFinished() && winner}>
                <ConfettiExplosion duration={5000} particleCount={200} className={styles.lotteryConfetti} />
            </VisibleIf>
            <div className={styles.lotteryAndRewardImageContainer}>
                <div
                    className={[
                        styles.lotteryTicketOuterContainer,
                        blur && styles.ticketOverlayed,
                        lotteryTypeDefinition.mainBackgroundStyle,
                    ].join(' ')}
                >
                    <div className={styles.lotteryTicketContainer}>{lotteryTicketList}</div>
                </div>
                {showEndImage && (
                    <div className={styles.endImageContainer}>
                        {didScratchAndWinOnCurrentTicket ? (
                            <Image className={styles.wonImage} src="scratch/did_win.svg" alt={'Gratulerer, du vant!'} />
                        ) : (
                            <Image
                                className={styles.lostImage}
                                src="scratch/did_not_win.svg"
                                alt={'Beklager, ingen premie'}
                            />
                        )}
                    </div>
                )}
            </div>
            <div className={styles.navigateButtons}>
                {index > 1 ? (
                    <RoundButton
                        onClick={gotoPreviousTicket}
                        aria-label={'Forrige lodd'}
                        className={[styles.navigateButton, styles.navigateLeft].join(' ')}
                    >
                        <i className="fa-solid fa-angle-left" aria-hidden="true"></i>
                    </RoundButton>
                ) : (
                    <div />
                )}
                <div>
                    Lodd {index} av {numberOfTickets}
                </div>
                {index < numberOfTickets && (
                    <RoundButton
                        onClick={gotoNextTicket}
                        aria-label={'Forrige lodd'}
                        className={[styles.navigateButton, styles.navigateRight].join(' ')}
                    >
                        <i className="fa-solid fa-angle-right" aria-hidden="true"></i>
                    </RoundButton>
                )}
            </div>
            {!hideScratchAll && !isFinished() && (
                <RoundedButtonPrimary
                    onClick={() => {
                        setScratchAll(true);
                    }}
                >
                    Skrap hele loddet
                </RoundedButtonPrimary>
            )}
        </>
    );
};

export default ScratchLotteryTicket;
